import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Funnel, MoreVertical, FileText } from "lucide-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  GetCandidateInterviewDetails,
  GetCandidateDetails,
  ScheduleNextInterview,
  SystemRemark,
  GetInterviewFeedbackByUID,
} from "../../../Candidates/CandidateServices/CandidateService";
import {
  getSentCandidateforJobEmp,
  viewResumeUrl,
} from "../Services/SendCandidateService";
import NormalAlert from "../../../../Alerts/NormalAlert";
import ViewResumeModal from "../Modals/ViewResumeModal";
import ColumnFilterModal from "../../../CommonModals/ColumnFilterModal";
import CandidateDetailsModal from "../../../Candidates/NewApplications/Modal/CandidateDetailsModal";
import Pagination from "../../../CommonModals/Pagination";
import InterviewFeedbackModal from "../../../Candidates/InterviewScheduled/Modal/InterviewFeedbackModal";
import ScheduleInterviewModal from "../../../Candidates/InterviewScheduled/Modal/ScheduleInterviewModal";
import AddInterviewerModal from "../../PostJob/Modal/AddInterviewerModal";
import {
  ShortlistCandidate,
  InterviewSchedule,
  SelectedCandidate,
  RejectedCandidates,
  HoldCandidates,
  StartBGV,
  SendAssessmentCandidate,
  OfferReleseCandidate,
  BackOutCandidate,
  HireCandidate,
} from "../../../Candidates/CandidateServices/CandidateService";
import { getEmployeeSuggestions } from "../../../../Auth/Services/metadataService";
import SystemRemarkModal from "../../../Candidates/NewApplications/Modal/SystemRemarkModal";

const GetSendCandidatesforJob = () => {
  // ROUTER
  const { jobUID } = useParams();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // STATE - DATA
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // STATE - UI
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUrl, setResumeUrl] = useState("");
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  // Candidate Details Modal States
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [interviewDetails, setInterviewDetails] = useState([]);
  const [modalType, setModalType] = useState("");
  const [showInterviewDetailsSection, setShowInterviewDetailsSection] =
    useState(false);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [interviewDateTime, setInterviewDateTime] = useState("");
  const [interviewerList, setInterviewerList] = useState([]);

  const [newInterviewer, setNewInterviewer] = useState({
    interviewerCode: "",
    interviewerName: "",
    interviewerEmail: "",
    interviewSequence: 1,
  });
  const [showAddInterviewer, setShowAddInterviewer] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const [assessmentLink, setAssessmentLink] = useState("");
  const [remark, setRemark] = useState("");

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);

  // STATE - SEARCH & FILTER
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const [systemRemarkData, setSystemRemarkData] = useState(null);
  const [showSystemRemark, setShowSystemRemark] = useState(false);
  const [showSystemRemarkModal, setShowSystemRemarkModal] = useState(false);
  const [systemRemarkLoading, setSystemRemarkLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [filters, setFilters] = useState({
    jobTitel: [],
    submitByType: [],
    functionName: [],
    department: [],
    grade: [],
  });

  const filterOptions = {
    jobTitel: [...new Set(candidates.map((c) => c.jobTitel))],
    submitByType: [...new Set(candidates.map((c) => c.submitByType))],
    functionName: [...new Set(candidates.map((c) => c.functionName))],
    department: [...new Set(candidates.map((c) => c.department))],
    grade: [...new Set(candidates.map((c) => c.grade))],
  };

  const filteredCandidates = candidates.filter((candidate) => {
    const matchesJobTitle =
      (filters.jobTitel || []).length === 0 ||
      (filters.jobTitel || []).includes(candidate.jobTitel);

    const matchesSubmitType =
      (filters.submitByType || []).length === 0 ||
      (filters.submitByType || []).includes(candidate.submitByType);

    const matchesFunction =
      (filters.functionName || []).length === 0 ||
      (filters.functionName || []).includes(candidate.functionName);

    const matchesDepartment =
      (filters.department || []).length === 0 ||
      (filters.department || []).includes(candidate.department);

    const matchesGrade =
      (filters.grade || []).length === 0 ||
      (filters.grade || []).includes(candidate.grade);

    const matchesSearch = [
      candidate.jobTitel,
      candidate.submitByType,
      candidate.functionName,
      candidate.department,
      candidate.grade,
      candidate.name,
      candidate.email,
    ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
      matchesJobTitle &&
      matchesSubmitType &&
      matchesFunction &&
      matchesDepartment &&
      matchesGrade &&
      matchesSearch
    );
  });
  // STATE - PAGINATION

  const [currentPage, setCurrentPage] = useState(1);
  const [candidatesPerPage, setCandidatesPerPage] = useState(10);

  // Pagination calculations
  const indexOfLast = currentPage * candidatesPerPage;
  const indexOfFirst = indexOfLast - candidatesPerPage;
  const currentCandidates = filteredCandidates.slice(indexOfFirst, indexOfLast);

  // Fetch candidates
  const fetchCandidates = async () => {
    try {
      const response = await getSentCandidateforJobEmp(jobUID);

      if (response?.data?.status === "Success" && response.data.candidateList) {
        setCandidates(response.data.candidateList);
      } else {
        setCandidates([]);
      }
    } catch (error) {
      const message = error?.response?.data?.message;

      if (message !== "No candidate found.") {
        setAlert({
          type: "fail",
          message: message || "Something went wrong. Please try again.",
        });
      }
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [jobUID]);

  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".action-menu")) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // HANDLERS

  const loadCandidateDetails = async (candidate) => {
    try {
      setSelectedCandidate(candidate);

      if (candidate.status === "Interview Scheduled") {
        const res = await GetCandidateInterviewDetails(
          candidate.submitedCandidateUID,
        );

        const data = res?.data || res;
        const finalData = data.interviewDetails || data;

        if (finalData?.status === "Success") {
          setCandidateDetails({
            candidateDetails: finalData.candidateDetails,
            jobDetails: finalData.jobDetails,
          });

          const interviewers = finalData.jobInterviewers || [];

          setInterviewerList(interviewers);
          setInterviewDetails(interviewers);
          setShowInterviewDetailsSection(true);

          setFeedbackData(finalData.feedbackDetails || null);
        }
      } else {
        const res = await GetCandidateDetails(candidate.submitedCandidateUID);

        if (res?.data?.status === "Success") {
          const data = res.data;

          setCandidateDetails(data);
          setInterviewerList(data.jobInterviewers || []);
          setInterviewDetails([]);
          setShowInterviewDetailsSection(false);

          setFeedbackData(null);
        }
      }
    } catch (err) {
      console.error(err);
      setAlert({
        type: "fail",
        message: "Failed to load candidate details.",
      });
    }
  };

  const handleNameClick = async (candidate) => {
    await loadCandidateDetails(candidate);
    setShowDetailsModal(true);
  };

  const handleScheduleClick = (interviewer) => {
    setSelectedInterview(interviewer);
    setShowDetailsModal(false);
    setShowScheduleModal(true);
  };

  const handleViewFeedback = async (interviewer) => {
    try {
      const res = await GetInterviewFeedbackByUID(
        selectedCandidate.submitedCandidateUID,
        interviewer.interviewUID,
      );

      if (res?.status === "Success" && res?.interviews?.length > 0) {
        setFeedbackData(res.interviews[0]);
      } else {
        setFeedbackData(null);
      }

      setShowDetailsModal(false);

      setTimeout(() => {
        setShowFeedbackModal(true);
      }, 100);
    } catch {
      setFeedbackData(null);
      setShowDetailsModal(false);
      setShowFeedbackModal(true);
    }
  };
  const formatDateTime = (dateTime) => {
    if (!dateTime) return null;

    const date = new Date(dateTime);
    const yyyy = date.getFullYear();
    const MM = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const HH = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    const fff = String(date.getMilliseconds()).padStart(3, "0");

    return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}.${fff}`;
  };

  const handleSchedule = async () => {
    if (!selectedInterview?.interviewUID) {
      setAlert({ type: "fail", message: "Interview UID missing" });
      return;
    }

    const formattedDate = formatDateTime(interviewDateTime);

    try {
      const response = await ScheduleNextInterview(
        selectedInterview.interviewUID,
        formattedDate,
      );

      if (response.data.status === "Success") {
        setAlert({ type: "success", message: response.data.message });
        setShowScheduleModal(false);
      } else {
        setAlert({ type: "fail", message: response.data.message });
      }
    } catch {
      setAlert({ type: "fail", message: "Failed to schedule interview" });
    }
  };

  const handleResumeClick = async (candidate) => {
    if (!candidate?.resumePath) {
      setAlert({
        type: "fail",
        message: "Resume not available.",
      });
      return;
    }

    try {
      const fileName = candidate.resumePath.split("/").pop();
      const url = await viewResumeUrl(fileName);

      setResumeUrl(url);
      setShowResumeModal(true);
    } catch (error) {
      setAlert({
        type: "fail",
        message: "Failed to load resume.",
      });
    }
  };

  const handleFinalAction = async () => {
    try {
      setActionLoading(true);

      let res;

      // Shortlist
      if (modalType === "shortlist") {
        res = await ShortlistCandidate(
          selectedCandidate.submitedCandidateUID,
          2,
          remark,
        );
      }

      // Interview Schedule
      if (modalType === "InterviewScheduled") {
        const formattedDateTime = interviewDateTime + ":00";

        const formattedInterviewers = interviewerList.map((i) => ({
          interviewerCode: i.interviewerCode,
          interviewSequence: String(i.interviewSequence),
        }));

        res = await InterviewSchedule(
          selectedCandidate.submitedCandidateUID,
          3,
          remark,
          formattedDateTime,
          formattedInterviewers,
        );
      }

      // Selected
      if (modalType === "Select") {
        res = await SelectedCandidate(
          selectedCandidate.submitedCandidateUID,
          5,
          remark,
        );
      }

      // Reject
      if (modalType === "Rejected" || modalType === "reject") {
        res = await RejectedCandidates(
          selectedCandidate.submitedCandidateUID,
          7,
          remark,
        );
      }

      // Hold
      if (modalType === "Hold") {
        res = await HoldCandidates(
          selectedCandidate.submitedCandidateUID,
          8,
          remark,
        );
      }

      // Start BGV
      if (modalType === "BGV") {
        res = await StartBGV(
          selectedCandidate.submitedCandidateUID,
          11,
          remark,
        );
      }

      // Send Assessment
      if (modalType === "Assessment") {
        res = await SendAssessmentCandidate(
          selectedCandidate.submitedCandidateUID,
          4,
          remark,
          assessmentLink,
        );
      }

      // Offer Release
      if (modalType === "OfferRelease") {
        res = await OfferReleseCandidate(
          selectedCandidate.submitedCandidateUID,
          9,
          remark,
        );
      }

      // Backout
      if (modalType === "Backout") {
        res = await BackOutCandidate(
          selectedCandidate.submitedCandidateUID,
          10,
          remark,
        );
      }

      // Hire
      if (modalType === "Hire") {
        res = await HireCandidate(
          selectedCandidate.submitedCandidateUID,
          6,
          remark,
        );
      }

      if (res?.data) {
        setAlert({
          type:
            res.data.status?.toLowerCase() === "success" ? "success" : "fail",
          message: res.data.message,
        });

        setShowDetailsModal(false);
        setModalType(null);
        setRemark("");

        // refresh table
        await fetchCandidates();
      }
    } catch (err) {
      console.error(err);
      setAlert({
        type: "fail",
        message: "Action failed",
      });
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openCandidateActionModal = async (candidate, actionType) => {
    await loadCandidateDetails(candidate);
    setModalType(actionType);
    setShowDetailsModal(true);
  };

  const handleViewSystemRemark = async () => {
    try {
      setSystemRemarkLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const response = await SystemRemark(
        selectedCandidate.submitedCandidateUID,
      );

      if (response?.data?.status === "Success") {
        setSystemRemarkData(response.data.data);
        setShowSystemRemarkModal(true);
      } else {
        setAlert({
          type: "fail",
          message: "Failed to fetch system remark",
        });
      }
    } catch (error) {
      setAlert({
        type: "fail",
        message: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setSystemRemarkLoading(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(interviewerList);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    const resequenced = items.map((it, i) => ({
      ...it,
      interviewSequence: String(i + 1),
    }));

    setInterviewerList(resequenced);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setNewInterviewer((prev) => ({
      ...prev,
      interviewerCode: value,
    }));

    if (name === "interviewerCode" && value.trim().length >= 3) {
      try {
        const res = await getEmployeeSuggestions(value.trim());

        if (res && res.length > 0) {
          setActiveField(name);
          setSuggestions(res);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setNewInterviewer((prev) => ({
      ...prev,
      interviewerCode: suggestion.employeeCode,
      interviewerName: suggestion.employeeName,
      interviewerEmail: suggestion.employeeEmail,
      interviewerDisplay: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
    }));

    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex]);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    try {
      return dateStr.split("T")[0];
    } catch {
      return "";
    }
  };

  return (
    <>
      {/* MAIN CONTAINER*/}
      <div className="w-full py-8 mt-12">
        <div className="max-w-[1240px] mx-auto">
          <div className="bg-white rounded-2xl px-6 py-6 shadow-sm">
            {/*  HEADER  */}
            <div className="bg-master-header text-white rounded-xl px-3 py-2 mb-6">
              <h3 className="font-bold text-lg">
                Sent Candidates For This Job
              </h3>
            </div>

            {/*  SEARCH  */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm  focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/*  TABLE  */}
            <div className="border rounded-lg overflow-visible">
              <table className="w-full">
                <thead className="bg-[#f5f8ff] text-sm uppercase">
                  <tr>
                    <th className="py-3 text-left w-[5%]">#</th>

                    <th className="py-3 text-left w-[20%]">Candidate Info</th>

                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Submitted By
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("submitByType")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[20%]">
                      <div className="flex items-center gap-2">
                        Role Info
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("jobTitel")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[12%]">
                      <div className="flex items-center gap-2">
                        Function
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("functionName")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    
                    <th className="py-3 text-left w-[12%]">
                      <div className="flex items-center gap-2">
                        Department
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("department")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[12%]">
                      <div className="flex items-center gap-2">
                        Grade
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("grade")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[20%]">Status</th>
                    {/* <th className="py-3">Submition Date</th> */}
                    <th className="py-3 text-left w-[20%]">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
                      {[...Array(5)].map((_, rowIdx) => (
                        <tr key={rowIdx} className="border-t">
                          {[...Array(9)].map((__, colIdx) => (
                            <td key={colIdx} className="py-4 px-3">
                              <Skeleton height={20} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </SkeletonTheme>
                  ) : currentCandidates.length ? (
                    currentCandidates.map((candidate, index) => (
                      <tr key={index} className="border-t">
                        <td className="py-3">{indexOfFirst + index + 1}</td>
                        {/* <td>{candidate.roleCode || "--"}</td> */}
                        <td className="cursor-pointer text-left">
                          <div className="flex flex-col items-start">
                            <span
                              className="text-blue-500 font-semibold"
                              onClick={() =>
                                handleNameClick(candidate.submitedCandidateUID)
                              }
                            >
                              {candidate.candidateName || "-"}
                            </span>

                            <span className="text-gray-600 text-sm">
                              {candidate.mobile || "-"}
                            </span>

                            <span className="text-gray-500 text-xs">
                              {candidate.email || "-"}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.submitedBy}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.submitByType}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {formatDate(candidate.submitDate)}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.roleCode || "-"}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.jobTitel || "-"}
                            </span>
                          </div>
                        </td>

                        {/* <td>{candidate.jobTitel}</td> */}
                        <td>{candidate.functionName}</td>
                        <td>{candidate.department}</td>
                        <td>{candidate.grade}</td>
                        <td>
                          <span className="inline-block max-w-[140px] truncate px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 whitespace-nowrap">
                            {candidate.status || "-"}
                          </span>
                        </td>
                        {/* <td>
                          {new Date(candidate.submitDate).toLocaleDateString(
                            "en-GB",
                          )}
                        </td> */}
                        <td className="px-3 text-center">
                          <div className="flex items-center justify-center gap-2 relative action-menu">
                            {/* Resume Icon */}
                            {candidate.resumePath && (
                              <button
                                onClick={() => handleResumeClick(candidate)}
                                className="p-1 hover:bg-gray-100 rounded-full"
                                title="View Resume"
                              >
                                <FileText size={18} className="text-blue-600" />
                              </button>
                            )}

                            {/* Three Dot Menu */}
                            {!["Back Out", "Hired", "Rejected"].includes(
                              candidate.status,
                            ) && (
                              <button
                                onClick={() =>
                                  setOpenMenu(openMenu === index ? null : index)
                                }
                                className="p-1 hover:bg-gray-100 rounded-full"
                              >
                                <MoreVertical size={20} />
                              </button>
                            )}

                            {/* Dropdown */}
                            {openMenu === index && (
                              <div className="absolute right-0 top-8 w-48 bg-white border rounded-lg shadow-lg z-50">
                                {candidate.status === "New" && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "shortlist",
                                        );
                                      }}
                                    >
                                      Shortlist
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "reject",
                                        );
                                      }}
                                    >
                                      Reject
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Hold",
                                        );
                                      }}
                                    >
                                      Hold
                                    </button>
                                  </>
                                )}

                                {candidate.status === "Shortlisted" && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "InterviewScheduled",
                                        );
                                      }}
                                    >
                                      Schedule Interview
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Rejected",
                                        );
                                      }}
                                    >
                                      Reject
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Hold",
                                        );
                                      }}
                                    >
                                      Hold
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Backout",
                                        );
                                      }}
                                    >
                                      Back-out
                                    </button>
                                  </>
                                )}

                                {candidate.status === "Interview Scheduled" && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => handleNameClick(candidate)}
                                    >
                                      Re-Schedule Interview
                                    </button>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Select",
                                        );
                                      }}
                                    >
                                      Select
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Rejected",
                                        );
                                      }}
                                    >
                                      Reject
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "BGV",
                                        );
                                      }}
                                    >
                                      Start BGV
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Backout",
                                        );
                                      }}
                                    >
                                      Back-out
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Hold",
                                        );
                                      }}
                                    >
                                      Hold
                                    </button>
                                  </>
                                )}

                                {candidate.status === "Selected" && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "OfferRelease",
                                        );
                                      }}
                                    >
                                      Release Offer
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Assessment",
                                        );
                                      }}
                                    >
                                      Send Assessment
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "BGV",
                                        );
                                      }}
                                    >
                                      Start BGV
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Backout",
                                        );
                                      }}
                                    >
                                      Back-out
                                    </button>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Hold",
                                        );
                                      }}
                                    >
                                      Hold
                                    </button>
                                  </>
                                )}

                                {candidate.status === "Hold" && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "InterviewScheduled",
                                        );
                                      }}
                                    >
                                      Schedule Interview
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Rejected",
                                        );
                                      }}
                                    >
                                      Reject
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Select",
                                        );
                                      }}
                                    >
                                      Select
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Backout",
                                        );
                                      }}
                                    >
                                      Back-out
                                    </button>
                                  </>
                                )}

                                {candidate.status === "Offer Released" && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Hire",
                                        );
                                      }}
                                    >
                                      Hire
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Backout",
                                        );
                                      }}
                                    >
                                      Back-out
                                    </button>
                                  </>
                                )}

                                {candidate.status === "BGV" && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Rejected",
                                        );
                                      }}
                                    >
                                      Reject
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Select",
                                        );
                                      }}
                                    >
                                      Select
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Backout",
                                        );
                                      }}
                                    >
                                      Back-out
                                    </button>

                                    <button
                                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                      onClick={() => {
                                        setOpenMenu(null);
                                        openCandidateActionModal(
                                          candidate,
                                          "Hold",
                                        );
                                      }}
                                    >
                                      Hold
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="py-6 text-red-500 text-center">
                        No Candidate Found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={filteredCandidates.length}
              itemsPerPage={candidatesPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setCandidatesPerPage}
            />
          </div>
        </div>
      </div>

      {/*  FILTER MODAL  */}
      <ColumnFilterModal
        show={!!activeFilter}
        onHide={() => setActiveFilter(null)}
        title={activeFilter}
        items={filterOptions[activeFilter] || []}
        filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
          item?.toLowerCase().includes(filterSearch.toLowerCase()),
        )}
        setFilteredItems={() => {}}
        selectedItems={filters[activeFilter] || []}
        setSelectedItems={(values) =>
          setFilters((prev) => ({
            ...prev,
            [activeFilter]: values,
          }))
        }
        searchText={filterSearch}
        setSearchText={setFilterSearch}
      />

      <CandidateDetailsModal
        show={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setShowSystemRemark(false);

          setModalType("");
          setRemark("");
          setInterviewDateTime("");
          setInterviewerList([]);
          setInterviewDetails([]);
          setCandidateDetails(null);
        }}
        candidateDetails={candidateDetails}
        selectedCandidate={selectedCandidate}
        interviewDetails={interviewDetails}
        modalActionType={modalType}
        showInterviewDetailsSection={showInterviewDetailsSection}
        showInterviewSection={modalType === "InterviewScheduled"}
        interviewerList={interviewerList}
        setInterviewerList={setInterviewerList}
        handleDragEnd={handleDragEnd}
        onOpenAddInterviewer={() => {
          setNewInterviewer({
            interviewerCode: "",
            interviewerName: "",
            interviewerEmail: "",
            interviewSequence: interviewerList.length + 1,
          });

          setShowAddInterviewer(true);
        }}
        // existing
        onScheduleClick={handleScheduleClick}
        onViewFeedback={handleViewFeedback}
        setInterviewDateTime={setInterviewDateTime}
        remark={remark}
        setRemark={setRemark}
        onActionSubmit={handleFinalAction}
        actionLoading={actionLoading}
        onViewSystemRemark={handleViewSystemRemark}
        systemRemarkData={systemRemarkData}
        showSystemRemark={showSystemRemark}
        showSystemRemarkButton={true}
        showResumeButton={true}
        onViewResume={() =>
          handleResumeClick({
            resumePath:
              selectedCandidate?.resumePath ||
              candidateDetails?.candidateDetails?.resumePath,
          })
        }
        systemRemarkLoading={systemRemarkLoading}
      />

      <SystemRemarkModal
        show={showSystemRemarkModal}
        onClose={() => setShowSystemRemarkModal(false)}
        systemRemarkData={systemRemarkData}
      />

      <AddInterviewerModal
        show={showAddInterviewer}
        onClose={() => setShowAddInterviewer(false)}
        newInterviewer={newInterviewer}
        setNewInterviewer={setNewInterviewer}
        handleChange={handleChange}
        handleKeyDown={handleKeyDown}
        handleSelectSuggestion={handleSelectSuggestion}
        showSuggestions={showSuggestions}
        activeField={activeField}
        suggestions={suggestions}
        highlightedIndex={highlightedIndex}
        setShowSuggestions={setShowSuggestions}
        setFormData={(callback) => {
          setInterviewerList((prev) => {
            const updated = callback({ jobInterviewers: prev });
            return updated.jobInterviewers;
          });
        }}
        formData={{ jobInterviewers: interviewerList }}
      />

      {/*  RESUME MODAL  */}
      <ViewResumeModal
        show={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        resumeUrl={resumeUrl}
      />

      <ScheduleInterviewModal
        show={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setShowDetailsModal(true);
        }}
        selectedInterview={selectedInterview}
        interviewDateTime={interviewDateTime}
        setInterviewDateTime={setInterviewDateTime}
        remark={remark}
        setRemark={setRemark}
        loading={loading}
        onSchedule={handleSchedule}
      />

      <InterviewFeedbackModal
        show={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setShowDetailsModal(true);
        }}
        feedbackData={feedbackData}
      />

      {/*  ALERT  */}
      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default GetSendCandidatesforJob;
